import { format } from 'date-fns';

import { DropdownOption } from 'types/dropdown';

import { cities as stateToCities } from '../../admin/components/location-details/cityMapping';

export const getCities = () =>
  Object.values(stateToCities)
    .flat()
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));

export const formatTime = (timestamp: string) =>
  format(new Date(timestamp), 'h:mm a');

export const formatDisplayDate = (dateString: string) => {
  const date = new Date(dateString);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const inputDate = new Date(date);
  inputDate.setHours(0, 0, 0, 0);

  if (inputDate.getTime() === today.getTime()) {
    return 'Today';
  }
  if (inputDate.getTime() === yesterday.getTime()) {
    return 'Yesterday';
  }
  return format(date, 'dd MMMM yyyy');
};

export const formatMobileNumber = (
  mobileNumber: string,
  countryCode: string
) => {
  if (!mobileNumber) return '';
  return (
    (countryCode
      ? `${countryCode.startsWith('+') ? '' : '+'}${countryCode}-`
      : '') + mobileNumber
  );
};

// Derive a rich dark text color from a given hex by preserving hue, setting a
// darker target lightness, and clamping saturation to a moderate range.
export const deriveDarkColor = (hex: string) => {
  const h = hex.replace('#', '');
  const full =
    h.length === 3
      ? h
          .split('')
          .map((c) => c + c)
          .join('')
      : h;
  const num = parseInt(full, 16);
  const r = Math.floor(num / 65536) % 256;
  const g = Math.floor(num / 256) % 256;
  const b = num % 256;

  // RGB -> HSL (all in [0,1])
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const delta = max - min;

  let hDeg = 0;
  const l = (max + min) / 2;
  const s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));
  if (delta !== 0) {
    switch (max) {
      case rn:
        hDeg = 60 * (((gn - bn) / delta) % 6);
        break;
      case gn:
        hDeg = 60 * ((bn - rn) / delta + 2);
        break;
      default:
        hDeg = 60 * ((rn - gn) / delta + 4);
    }
  }
  if (hDeg < 0) hDeg += 360;

  // Near-gray fallback to a neutral dark
  if (s < 0.06) {
    return '#3d3d3d';
  }

  // Calibrate target L and saturation clamp ranges by hue family
  let targetL = 0.2;
  let minS = 0.3;
  let maxS = 0.4;
  if (hDeg >= 260 && hDeg < 320) {
    // Purples
    targetL = 0.24;
    minS = 0.32;
    maxS = 0.4;
  } else if (hDeg >= 20 && hDeg < 80) {
    // Warm yellows/oranges
    targetL = 0.18;
    minS = 0.3;
    maxS = 0.38;
  } else if (hDeg >= 90 && hDeg < 160) {
    // Greens
    targetL = 0.18;
    minS = 0.24;
    maxS = 0.34;
  } else if (hDeg >= 320 || hDeg < 20) {
    // Reds
    targetL = 0.2;
    minS = 0.32;
    maxS = 0.42;
  }

  const S = Math.max(minS, Math.min(maxS, s * 1.25));
  const L = targetL;

  // HSL -> RGB
  const C = (1 - Math.abs(2 * L - 1)) * S;
  const X = C * (1 - Math.abs(((hDeg / 60) % 2) - 1));
  const m = L - C / 2;

  let r1 = 0;
  let g1 = 0;
  let b1 = 0;
  if (hDeg >= 0 && hDeg < 60) [r1, g1, b1] = [C, X, 0];
  else if (hDeg >= 60 && hDeg < 120) [r1, g1, b1] = [X, C, 0];
  else if (hDeg >= 120 && hDeg < 180) [r1, g1, b1] = [0, C, X];
  else if (hDeg >= 180 && hDeg < 240) [r1, g1, b1] = [0, X, C];
  else if (hDeg >= 240 && hDeg < 300) [r1, g1, b1] = [X, 0, C];
  else [r1, g1, b1] = [C, 0, X];

  const R = Math.round((r1 + m) * 255);
  const G = Math.round((g1 + m) * 255);
  const B = Math.round((b1 + m) * 255);

  return `#${[R, G, B].map((v) => v.toString(16).padStart(2, '0')).join('')}`;
};

export const isAllOptionsSelected = (
  selectedOptions: DropdownOption[],
  options: DropdownOption[]
) => selectedOptions.length === options.length;

export const getTableColumns = <T extends { accessor: string }>(
  columns: T[],
  showActions: boolean
): T[] =>
  showActions
    ? columns
    : columns.filter((column) => column.accessor !== 'actions');
